import { creditBlockReference, creditDetailsLink } from "./creditBookingLink.js";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { formatDate, formatTime } from "../format.js";
import { Pagination } from "../Pagination.js";
import { useAsync } from "../useAsync.js";
import { ScrollableTabs } from "../ui/Tabs.js";
import {
  booking,
  type BookingHistoryPage,
  type LessonCreditActivity,
  type LessonCreditSummary,
  type OperationHistoryEntry,
  type PurchaseHistoryEntry,
} from "./api.js";
import { paymentMethodLabel } from "./methods.js";
import { formatMoney } from "./money.js";
import { operationOutcome, operationTitle } from "./policy.js";
import { CARD_RECORD_PAGE_SIZE } from "../paginationPageSizes.js";
import "./familyHistory.css";

export const FAMILY_HISTORY_LABEL = "Payments & requests";
const CREDIT_ACTIVITY_PAGE_SIZE = 50;

type HistoryTab = "payments" | "requests" | "credits";

const FAMILY_HISTORY_SCOPE_KEY = "familyHistoryBrowse";
const FAMILY_HISTORY_COLLECTION = "booking-history";
const FAMILY_HISTORY_PAGE_SIZE = CARD_RECORD_PAGE_SIZE;

interface FamilyHistoryScope {
  accountId: string;
  studentId: string;
  collection: typeof FAMILY_HISTORY_COLLECTION;
}

interface FamilyHistoryBrowse {
  tab: HistoryTab;
  blockId: string | null;
  paymentsPage: number;
  requestsPage: number;
}

interface FamilyHistoryPages {
  paymentsPage: number;
  requestsPage: number;
}

interface LoadedFamilyHistory {
  scopeKey: string;
  requestKey: string;
  dataKey: string;
  pages: FamilyHistoryPages;
  history: BookingHistoryPage;
}

interface PendingFamilyHistoryPages extends FamilyHistoryPages {
  scopeKey: string;
  routeKey: string;
  replace: boolean;
}

interface FamilyHistoryPageError {
  scopeKey: string;
  routeKey: string;
  failedRequestKey: string;
  fallbackRequestKey: string | null;
}

function readHistoryPage(value: string | null): number {
  if (value === null) return 1;
  const page = Number(value);
  return Number.isSafeInteger(page)
    && page >= 1
    && page <= Math.floor(Number.MAX_SAFE_INTEGER / FAMILY_HISTORY_PAGE_SIZE)
    ? page
    : 1;
}

function historyPageCount(total: number): number {
  return Math.max(1, Math.ceil(total / FAMILY_HISTORY_PAGE_SIZE));
}

function familyHistoryRequestKey(scopeKey: string, pages: FamilyHistoryPages): string {
  return `${scopeKey}:${pages.paymentsPage}:${pages.requestsPage}`;
}

function samePages(left: FamilyHistoryPages, right: FamilyHistoryPages): boolean {
  return left.paymentsPage === right.paymentsPage
    && left.requestsPage === right.requestsPage;
}

async function fetchFamilyHistoryPage(
  studentId: string,
  scopeKey: string,
  requestedPages: FamilyHistoryPages,
): Promise<LoadedFamilyHistory> {
  const requestKey = familyHistoryRequestKey(scopeKey, requestedPages);
  let pages = requestedPages;

  // If the collection shrinks between visits, fetch its new last page before
  // returning. Totals and rows always describe the same server response.
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const history = await booking.historyPage(studentId, {
      purchaseLimit: FAMILY_HISTORY_PAGE_SIZE,
      purchaseOffset: (pages.paymentsPage - 1) * FAMILY_HISTORY_PAGE_SIZE,
      operationLimit: FAMILY_HISTORY_PAGE_SIZE,
      operationOffset: (pages.requestsPage - 1) * FAMILY_HISTORY_PAGE_SIZE,
    });
    const actualPages = {
      paymentsPage: Math.min(pages.paymentsPage, historyPageCount(history.purchase_total)),
      requestsPage: Math.min(pages.requestsPage, historyPageCount(history.operation_total)),
    };
    if (samePages(actualPages, pages)) {
      return {
        scopeKey,
        requestKey,
        dataKey: familyHistoryRequestKey(scopeKey, pages),
        pages,
        history,
      };
    }
    pages = actualPages;
  }

  throw new Error("Payment and request history changed while loading. Please try again.");
}

function readBlockId(value: string | null): string | null {
  return value && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
    ? value
    : null;
}

function readHistoryTab(value: string | null): HistoryTab {
  return value === "requests" || value === "credits" ? value : "payments";
}

function familyHistoryScopeFromState(state: unknown): unknown | null {
  if (typeof state !== "object" || state === null || Array.isArray(state)) return null;
  if (!Object.prototype.hasOwnProperty.call(state, FAMILY_HISTORY_SCOPE_KEY)) return null;
  return (state as Record<string, unknown>)[FAMILY_HISTORY_SCOPE_KEY] ?? {};
}

function isFamilyHistoryScope(value: unknown, scope: FamilyHistoryScope): boolean {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const candidate = value as Partial<FamilyHistoryScope>;
  return candidate.accountId === scope.accountId
    && candidate.studentId === scope.studentId
    && candidate.collection === scope.collection;
}

function familyHistoryRouteState(state: unknown, scope: FamilyHistoryScope): Record<string, unknown> {
  const current = typeof state === "object" && state !== null && !Array.isArray(state)
    ? state as Record<string, unknown>
    : {};
  return { ...current, [FAMILY_HISTORY_SCOPE_KEY]: scope };
}

function familyHistorySearch(search: string, browse: FamilyHistoryBrowse): string {
  const current = new URLSearchParams(search);
  const next = new URLSearchParams();
  const studentId = current.get("student");
  if (studentId) next.set("student", studentId);
  if (browse.tab !== "payments") next.set("tab", browse.tab);
  if (browse.tab === "credits" && browse.blockId) next.set("block_id", browse.blockId);
  if (browse.paymentsPage > 1) next.set("payments_page", String(browse.paymentsPage));
  if (browse.requestsPage > 1) next.set("requests_page", String(browse.requestsPage));
  const query = next.toString();
  return query ? `?${query}` : "";
}

const HISTORY_TABS: Array<{ id: HistoryTab; label: string }> = [
  { id: "payments", label: "Payments" },
  { id: "requests", label: "Requests" },
  { id: "credits", label: "Credits" },
];

export function historyTabForKey(
  current: HistoryTab,
  key: string,
): HistoryTab | null {
  const currentIndex = HISTORY_TABS.findIndex((tab) => tab.id === current);
  if (currentIndex < 0) return null;

  if (key === "Home") return HISTORY_TABS[0]?.id ?? null;
  if (key === "End") return HISTORY_TABS.at(-1)?.id ?? null;
  if (key !== "ArrowLeft" && key !== "ArrowRight") return null;

  const direction = key === "ArrowRight" ? 1 : -1;
  const nextIndex =
    (currentIndex + direction + HISTORY_TABS.length) % HISTORY_TABS.length;
  return HISTORY_TABS[nextIndex]?.id ?? null;
}

/**
 * The family's payment and booking-request paper trail.
 *
 * Lessons themselves still belong on Home / the lesson lists. This workspace
 * answers a different question: what money or credit record exists, and what
 * happened to a booking request even when that request never produced a lesson.
 */
export function History({
  accountId,
  studentId,
  studentName,
}: {
  accountId: string;
  studentId: string;
  studentName: string;
}) {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const scope: FamilyHistoryScope = {
    accountId,
    studentId,
    collection: FAMILY_HISTORY_COLLECTION,
  };
  const historyScope = familyHistoryScopeFromState(location.state);
  const scopeChanged = historyScope !== null && !isFamilyHistoryScope(historyScope, scope);
  const stateMatchesScope = isFamilyHistoryScope(historyScope, scope);
  const browse: FamilyHistoryBrowse = {
    tab: scopeChanged ? "payments" : readHistoryTab(searchParams.get("tab")),
    blockId: scopeChanged ? null : readBlockId(searchParams.get("block_id")),
    paymentsPage: scopeChanged ? 1 : readHistoryPage(searchParams.get("payments_page")),
    requestsPage: scopeChanged ? 1 : readHistoryPage(searchParams.get("requests_page")),
  };

  useEffect(() => {
    const safeSearch = familyHistorySearch(location.search, browse);
    if (location.search !== safeSearch || !stateMatchesScope) {
      setSearchParams(new URLSearchParams(safeSearch), {
        replace: true,
        state: familyHistoryRouteState(location.state, scope),
      });
    }
  }, [
    browse.paymentsPage,
    browse.requestsPage,
    browse.tab,
    browse.blockId,
    location.pathname,
    location.search,
    location.state,
    scope.accountId,
    scope.studentId,
    setSearchParams,
    stateMatchesScope,
  ]);

  const updateBrowse = useCallback((
    next: Partial<FamilyHistoryBrowse>,
    options?: { replace?: boolean },
  ) => {
    const nextBrowse = { ...browse, ...next };
    setSearchParams(new URLSearchParams(familyHistorySearch(location.search, nextBrowse)), {
      replace: options?.replace === true,
      state: familyHistoryRouteState(location.state, scope),
    });
  }, [
    browse.paymentsPage,
    browse.requestsPage,
    browse.tab,
    browse.blockId,
    location.search,
    location.state,
    scope.accountId,
    scope.studentId,
    setSearchParams,
  ]);

  return (
    <>
      <h1>{FAMILY_HISTORY_LABEL}</h1>
      <p className="lede muted">
        Payments, lesson credits and booking requests for <strong>{studentName}</strong>.
      </p>
      <Loaded
        accountId={accountId}
        studentId={studentId}
        studentName={studentName}
        routeKey={location.key}
        browse={browse}
        onBrowseChange={updateBrowse}
      />
    </>
  );
}

function Loaded({
  accountId,
  studentId,
  studentName,
  routeKey,
  browse,
  onBrowseChange,
}: {
  accountId: string;
  studentId: string;
  studentName: string;
  routeKey: string;
  browse: FamilyHistoryBrowse;
  onBrowseChange: (next: Partial<FamilyHistoryBrowse>, options?: { replace?: boolean }) => void;
}) {
  const scopeKey = JSON.stringify([accountId, studentId]);
  const creditActivityKey = JSON.stringify([accountId, studentId, browse.blockId]);
  const browsePages: FamilyHistoryPages = {
    paymentsPage: browse.paymentsPage,
    requestsPage: browse.requestsPage,
  };
  const [pendingPages, setPendingPages] = useState<PendingFamilyHistoryPages | null>(null);
  const [pageError, setPageError] = useState<FamilyHistoryPageError | null>(null);
  const [creditActivityPage, setCreditActivityPage] = useState<{ key: string; offset: number }>({ key: "", offset: 0 });
  const activityOffset = creditActivityPage.key === creditActivityKey ? creditActivityPage.offset : 0;
  const activePending = pendingPages?.routeKey === routeKey
    && pendingPages.scopeKey === scopeKey
    ? pendingPages
    : null;
  const requestedPages = activePending ?? browsePages;
  const requestKey = familyHistoryRequestKey(scopeKey, requestedPages);
  const history = useAsync(
    () => fetchFamilyHistoryPage(studentId, scopeKey, requestedPages),
    [accountId, studentId, requestedPages.paymentsPage, requestedPages.requestsPage],
  );
  const creditReads = useAsync(
    () => browse.tab !== "requests" ? booking.credits(studentId) : Promise.resolve(null),
    [accountId, studentId, browse.tab !== "requests"],
  );
  const creditActivityReads = useAsync(
    () => browse.tab === "credits" && browse.blockId
      ? booking.creditActivity(studentId, browse.blockId, { limit: CREDIT_ACTIVITY_PAGE_SIZE, offset: activityOffset })
      : Promise.resolve(null),
    [accountId, studentId, browse.tab, browse.blockId, activityOffset],
  );
  const creditSummary = creditReads.data?.portal_student_id === studentId ? creditReads.data : null;
  const creditActivity = creditActivityReads.data?.portal_student_id === studentId
    && creditActivityReads.data.lesson_block_id.toLowerCase() === browse.blockId?.toLowerCase()
    && creditActivityReads.data.offset === activityOffset
    ? creditActivityReads.data
    : null;
  const changeActivityOffset = useCallback((offset: number) => {
    setCreditActivityPage({ key: creditActivityKey, offset: Math.max(0, offset) });
  }, [creditActivityKey]);
  const loaded = history.data?.scopeKey === scopeKey ? history.data : null;
  const requestedResult = loaded && (
    loaded.requestKey === requestKey || loaded.dataKey === requestKey
  ) ? loaded : null;

  useEffect(() => {
    if (pendingPages && (pendingPages.routeKey !== routeKey || pendingPages.scopeKey !== scopeKey)) {
      setPendingPages(null);
    }
    if (pageError && pageError.scopeKey !== scopeKey) setPageError(null);
  }, [pageError, pendingPages, routeKey, scopeKey]);

  useEffect(() => {
    if (history.loading) return;

    if (history.error) {
      if (activePending) {
        setPageError({
          scopeKey,
          routeKey,
          failedRequestKey: requestKey,
          fallbackRequestKey: loaded?.dataKey ?? null,
        });
        setPendingPages((current) => current === activePending ? null : current);
      } else if (loaded && loaded.dataKey !== requestKey) {
        setPageError({
          scopeKey,
          routeKey,
          failedRequestKey: requestKey,
          fallbackRequestKey: loaded.dataKey,
        });
        // Back/forward can select a page too. If it cannot be loaded, restore
        // the visible page in place so the URL and pager continue to agree.
        onBrowseChange(loaded.pages, { replace: true });
      }
      return;
    }

    if (!requestedResult) return;
    if (activePending) {
      onBrowseChange(requestedResult.pages, { replace: activePending.replace });
      setPendingPages((current) => current === activePending ? null : current);
    } else if (!samePages(requestedResult.pages, browsePages)) {
      // Correcting a stale URL replaces the entry so Back cannot re-clamp it.
      onBrowseChange(requestedResult.pages, { replace: true });
    }

    setPageError((current) => {
      if (!current || current.scopeKey !== scopeKey) return null;
      if (requestKey === current.failedRequestKey) return null;
      if (routeKey !== current.routeKey && requestKey !== current.fallbackRequestKey) return null;
      return current;
    });
  }, [
    activePending,
    browsePages.paymentsPage,
    browsePages.requestsPage,
    history.error,
    history.loading,
    loaded,
    onBrowseChange,
    requestKey,
    requestedResult,
    routeKey,
    scopeKey,
  ]);

  const visibleHistory = loaded;
  const displayError = pageError
    ? "Could not load that history page. Showing the previously loaded results."
    : history.error && visibleHistory && visibleHistory.dataKey !== requestKey
      ? "Could not load that history page. Showing the previously loaded results."
      : null;

  if (history.loading && !visibleHistory)
    return <p className="empty">Loading…</p>;
  if (history.error && !visibleHistory) return <div className="error" role="alert">
    We could not load booking history right now. <button type="button" className="quiet small" onClick={history.reload}>Retry history</button>
  </div>;
  if (!visibleHistory) return null;

  return (
    <>
      {history.loading && requestKey !== visibleHistory.dataKey && (
        <p className="small muted" role="status">Loading the selected history page…</p>
      )}
      <HistoryContent
        studentId={studentId}
        studentName={studentName}
        purchases={visibleHistory.history.purchases}
        purchaseTotal={visibleHistory.history.purchase_total}
        operations={visibleHistory.history.operations}
        operationTotal={visibleHistory.history.operation_total}
        browse={{ ...browse, ...visibleHistory.pages }}
        creditSummary={creditSummary}
        creditActivity={creditActivity}
        creditActivityOffset={activityOffset}
        onChangeCreditActivityOffset={changeActivityOffset}
        creditLoading={browse.tab === "credits" && creditReads.loading}
        creditError={browse.tab === "credits" ? creditReads.error : null}
        creditBlockId={browse.blockId}
        onRetryCredits={creditReads.reload}
        creditActivityLoading={browse.tab === "credits" && Boolean(browse.blockId) && creditActivityReads.loading}
        creditActivityError={browse.tab === "credits" && browse.blockId ? creditActivityReads.error : null}
        onRetryCreditActivity={creditActivityReads.reload}
        loadingPage={history.loading && requestKey !== visibleHistory.dataKey}
        onTabChange={(tab) => {
          setPageError(null);
          onBrowseChange({ tab, blockId: tab === "credits" ? browse.blockId : null });
        }}
        onPaymentsPageChange={(paymentsPage) => {
          setPageError(null);
          setPendingPages({
            ...(activePending ?? browsePages),
            paymentsPage,
            scopeKey,
            routeKey,
            replace: false,
          });
        }}
        onRequestsPageChange={(requestsPage) => {
          setPageError(null);
          setPendingPages({
            ...(activePending ?? browsePages),
            requestsPage,
            scopeKey,
            routeKey,
            replace: false,
          });
        }}
      />
      {displayError && <div className="error" role="alert">{displayError} <button type="button" className="quiet small" onClick={history.reload}>Retry this page</button></div>}
    </>
  );
}

/**
 * Separated from the fetch so the two family-facing views can be exercised as
 * UI in isolation. The API remains the source of truth; this component only
 * chooses which already-returned records to show.
 */
export function HistoryContent({
  studentId,
  studentName,
  purchases,
  purchaseTotal = purchases.length,
  operations,
  operationTotal = operations.length,
  creditSummary = null,
  creditActivity = null,
  creditActivityOffset = 0,
  onChangeCreditActivityOffset,
  creditLoading = false,
  creditError = null,
  creditBlockId = null,
  creditActivityLoading = false,
  creditActivityError = null,
  initialTab = "payments",
  browse,
  loadingPage = false,
  onTabChange,
  onPaymentsPageChange,
  onRequestsPageChange,
  onRetryCredits,
  onRetryCreditActivity,
}: {
  studentId?: string;
  studentName: string;
  purchases: PurchaseHistoryEntry[];
  purchaseTotal?: number;
  operations: OperationHistoryEntry[];
  operationTotal?: number;
  creditSummary?: LessonCreditSummary | null;
  creditActivity?: LessonCreditActivity | null;
  creditActivityOffset?: number;
  onChangeCreditActivityOffset?: (offset: number) => void;
  creditLoading?: boolean;
  creditError?: string | null;
  creditBlockId?: string | null;
  creditActivityLoading?: boolean;
  creditActivityError?: string | null;
  initialTab?: HistoryTab;
  browse?: FamilyHistoryBrowse;
  loadingPage?: boolean;
  onTabChange?: (tab: HistoryTab) => void;
  onPaymentsPageChange?: (page: number) => void;
  onRequestsPageChange?: (page: number) => void;
  onRetryCredits?: () => void;
  onRetryCreditActivity?: () => void;
}) {
  const [localTab, setLocalTab] = useState<HistoryTab>(initialTab);
  const [localPaymentsPage, setLocalPaymentsPage] = useState(1);
  const [localRequestsPage, setLocalRequestsPage] = useState(1);
  const activeTab = browse?.tab ?? localTab;
  const [focusedTab, setFocusedTab] = useState<HistoryTab>(initialTab);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const paymentPages = historyPageCount(purchaseTotal);
  const requestPages = historyPageCount(operationTotal);
  const paymentPage = Math.min(browse?.paymentsPage ?? localPaymentsPage, paymentPages);
  const requestPage = Math.min(browse?.requestsPage ?? localRequestsPage, requestPages);

  useEffect(() => {
    setFocusedTab(activeTab);
  }, [activeTab]);

  function chooseTab(tab: HistoryTab) {
    if (onTabChange) onTabChange(tab);
    else setLocalTab(tab);
    setFocusedTab(tab);
  }

  function changePaymentsPage(page: number) {
    if (onPaymentsPageChange) onPaymentsPageChange(page);
    else setLocalPaymentsPage(Math.min(paymentPages, Math.max(1, page)));
  }

  function changeRequestsPage(page: number) {
    if (onRequestsPageChange) onRequestsPageChange(page);
    else setLocalRequestsPage(Math.min(requestPages, Math.max(1, page)));
  }

  function onTabKeyDown(
    event: ReactKeyboardEvent<HTMLButtonElement>,
    currentTab: HistoryTab,
  ) {
    const nextTab = historyTabForKey(currentTab, event.key);
    if (nextTab === null) return;
    event.preventDefault();
    const nextIndex = HISTORY_TABS.findIndex((tab) => tab.id === nextTab);
    setFocusedTab(nextTab);
    tabRefs.current[nextIndex]?.focus();
  }

  return (
    <div className="panel history-workspace">
      <ScrollableTabs
        className="history-tabs"
        ariaLabel={`${FAMILY_HISTORY_LABEL} for ${studentName}`}
      >
        {HISTORY_TABS.map((tab, index) => (
          <button
            id={`history-${tab.id}-tab`}
            className="history-tab"
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`history-${tab.id}-panel`}
            tabIndex={focusedTab === tab.id ? 0 : -1}
            key={tab.id}
            ref={(node) => {
              tabRefs.current[index] = node;
            }}
            onFocus={() => setFocusedTab(tab.id)}
            onKeyDown={(event) => onTabKeyDown(event, tab.id)}
            onClick={() => chooseTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </ScrollableTabs>

      <section
        id="history-payments-panel"
        className="history-section"
        role="tabpanel"
        aria-labelledby="history-payments-tab"
        hidden={activeTab !== "payments"}
      >
        {activeTab === "payments" && (
          <>
            <h2>Payments</h2>
            <p className="small muted history-section-intro">
              Payment and lesson-credit records for {studentName}. The Hub
              records the payment state; it does not charge a card here.
            </p>
            {purchaseTotal === 0 ? (
              <p className="empty">No payment history yet</p>
            ) : (
              <>
                {purchases.length === 0 && (
                  <p className="empty">No payment records on this page</p>
                )}
                {purchases.map((purchase) => (
                  <Purchase
                    key={purchase.lesson_block_id}
                    purchase={purchase}
                    studentName={studentName}
                    studentId={studentId}
                    blockStatus={creditSummary?.blocks.find(block => block.lesson_block_id.toLowerCase() === purchase.lesson_block_id.toLowerCase())?.status ?? null}
                  />
                ))}
                <Pagination
                  page={paymentPage}
                  pages={paymentPages}
                  disabled={loadingPage}
                  onPageChange={changePaymentsPage}
                  ariaLabel="Payment history pagination"
                />
              </>
            )}
          </>
        )}
      </section>

      <section
        id="history-requests-panel"
        className="history-section"
        role="tabpanel"
        aria-labelledby="history-requests-tab"
        hidden={activeTab !== "requests"}
      >
        {activeTab === "requests" && (
          <>
            <h2>Requests</h2>
            <p className="small muted history-section-intro">
              Booking requests for {studentName}, including requests that could
              not be completed.
            </p>
            {operationTotal === 0 ? (
              <p className="empty">No booking requests yet</p>
            ) : (
              <>
                {operations.length === 0 && (
                  <p className="empty">No requests on this page</p>
                )}
                {operations.map((operation) => (
                  <Operation
                    key={operation.operation_id}
                    operation={operation}
                    studentName={studentName}
                  />
                ))}
                <Pagination
                  page={requestPage}
                  pages={requestPages}
                  disabled={loadingPage}
                  onPageChange={changeRequestsPage}
                  ariaLabel="Booking request history pagination"
                />
              </>
            )}
          </>
        )}
      </section>

      <section
        id="history-credits-panel"
        className="history-section"
        role="tabpanel"
        aria-labelledby="history-credits-tab"
        hidden={activeTab !== "credits"}
      >
        {activeTab === "credits" && <CreditDetails
          summary={creditSummary}
          activity={creditActivity}
          activityOffset={creditActivityOffset}
          onChangeActivityOffset={onChangeCreditActivityOffset}
          loading={creditLoading}
          error={creditError}
          blockId={creditBlockId}
          onRetry={onRetryCredits}
          activityLoading={creditActivityLoading}
          activityError={creditActivityError}
          onRetryActivity={onRetryCreditActivity}
        />}
      </section>
    </div>
  );
}

function CreditDetails({
  summary,
  activity,
  activityOffset,
  onChangeActivityOffset,
  loading,
  error,
  blockId,
  onRetry,
  activityLoading,
  activityError,
  onRetryActivity,
}: {
  summary: LessonCreditSummary | null;
  activity: LessonCreditActivity | null;
  activityOffset: number;
  onChangeActivityOffset?: (offset: number) => void;
  loading: boolean;
  error: string | null;
  blockId: string | null;
  onRetry?: () => void;
  activityLoading: boolean;
  activityError: string | null;
  onRetryActivity?: () => void;
}) {
  const blocks = summary?.blocks ?? [];
  const selected = blockId ? blocks.find((block) => block.lesson_block_id.toLowerCase() === blockId.toLowerCase()) : null;
  const visible = blockId ? (selected ? [selected] : []) : blocks;
  return <>
    <h2>Lesson credits</h2>
    <p className="small muted history-section-intro">
      Available to book is the amount the booking service currently permits. The ledger balance excludes credits already used to book lessons. Booking holds are separate while a booking is being confirmed.
    </p>
    {blockId && <p><Link to={creditDetailsLink(summary?.portal_student_id)}>View all credit blocks</Link></p>}
    {loading && !summary && <p className="small muted" role="status">Loading credit details…</p>}
    {error && <div className="error" role="alert">Credit details could not be loaded.
      {onRetry && <> <button type="button" className="quiet small" onClick={onRetry}>Retry credit details</button></>}
    </div>}
    {!loading && !error && blockId && !selected && <p className="empty">That credit block is not available for this learner.</p>}
    {!loading && !error && !blockId && blocks.length === 0 && <p className="empty">No credit blocks are available for this learner.</p>}
    {visible.map((block) => {
      const id = block.lesson_block_id;
      const countsKnown = Number.isSafeInteger(block.credits_purchased)
        && Number.isSafeInteger(block.consumed_credits)
        && Number.isSafeInteger(block.remaining_credits);
      const reconciles = countsKnown
        && block.credits_purchased === block.consumed_credits! + block.remaining_credits!;
      const held = Number.isSafeInteger(block.booking_held_credits) ? block.booking_held_credits! : null;
      const breakdownKnown = Number.isSafeInteger(block.upcoming_allocated_credits)
        && Number.isSafeInteger(block.completed_used_credits);
      const closedLabel = closedBlockLabel(block.status);
      const balanceUnresolved = (block.ledger_balance != null && block.ledger_balance < 0)
        || (held !== null && block.ledger_balance != null && held > block.ledger_balance);
      return <article className="panel history-credit-block" id={`credit-block-${id}`} key={id}>
        <h3>{block.service_name}</h3>
        <p className="small muted">Block reference {creditBlockReference(id)}</p>
        <p className="small muted">Purchased {block.purchased_at ? formatDate(block.purchased_at) : "date unavailable"} · Status: {creditBlockStatusLabel(block.status)}</p>
        {closedLabel && <p className="notice small" role="status">
          {block.status === "refunded" ? "This block was refunded" : "This block was cancelled"}, so its lessons can’t be booked. Any balance shown in the activity history is not available to use.
        </p>}
        <dl className="history-credit-counts">
          {block.credits_purchased !== undefined && <div><dt>Lessons purchased</dt><dd>{block.credits_purchased}</dd></div>}
          {!closedLabel && block.consumed_credits !== undefined && <div><dt>Net credits used</dt><dd>{block.consumed_credits}</dd></div>}
          {!closedLabel && block.remaining_credits !== undefined && <div><dt>Remaining in this block</dt><dd>{block.remaining_credits}</dd></div>}
          {!closedLabel && held !== null && <div><dt>Held while bookings are confirmed</dt><dd>{held}</dd></div>}
          {!closedLabel && breakdownKnown && <><div><dt>Allocated to upcoming lessons</dt><dd>{block.upcoming_allocated_credits}</dd></div><div><dt>Completed lesson usage</dt><dd>{block.completed_used_credits}</dd></div></>}
          {block.ledger_balance != null && block.ledger_balance < 0 && <div><dt>Ledger balance — needs review</dt><dd>{block.ledger_balance}</dd></div>}
          <div><dt>Available to book</dt><dd>{block.available_credits}</dd></div>
        </dl>
        <p className="small">Expires {formatDate(block.expires_at)}.</p>
        {!closedLabel && !breakdownKnown && <p className="small muted">Upcoming and completed usage cannot yet be separated by this service. Net credits used includes bookings for future lessons and other ledger changes; it is not a completed-lesson count.</p>}
        {!closedLabel && ((countsKnown && !reconciles) || balanceUnresolved) && <p className="notice small" role="status">These block counts do not reconcile. Please ask the school to review this balance.</p>}
        {!closedLabel && !block.booking.allowed && block.booking.reasons.length > 0 && <ul className="small">{block.booking.reasons.map((reason) => <li key={reason.code}>{reason.message}</li>)}</ul>}
        {!closedLabel && block.booking.allowed === false && block.booking.reasons.length === 0 && <p className="small muted">This block cannot currently be booked online. Ask the school to check its status.</p>}
        {blockId ? <details className="history-credit-activity">
          <summary aria-label={`Credit activity for ${block.service_name}, purchased ${block.purchased_at ? formatDate(block.purchased_at) : "date unavailable"}`}>Credit activity</summary>
          <div className="history-credit-activity-content">
            {activityLoading && !activity && <p className="small muted" role="status">Loading credit activity…</p>}
            {activityError && <div className="error" role="alert">Credit activity could not be loaded.
              {onRetryActivity && <> <button type="button" className="quiet small" onClick={onRetryActivity}>Retry activity</button></>}
            </div>}
            {!activityLoading && !activityError && activity && activity.entries.length === 0 && <p className="small muted">{activityOffset === 0 ? "No transaction activity is recorded for this block." : "No older activity is available at this position."}</p>}
            {activity && activity.entries.length > 0 && <ol className="history-credit-activity-list">
              {activity.entries.map((entry) => <li key={entry.transaction_id}>
                <strong>{creditActivityLabel(entry.transaction_type)}</strong>
                <time className="small muted" dateTime={entry.created_at}>{formatDate(entry.created_at)}</time>
                <span className="small">{entry.credit_delta > 0 ? "+" : "−"}{Math.abs(entry.credit_delta)} {Math.abs(entry.credit_delta) === 1 ? "lesson" : "lessons"}</span>
                {entry.amelia_appointment_id && <span className="small muted">Appointment reference {entry.amelia_appointment_id}</span>}
              </li>)}
            </ol>}
            {activity && activity.entries.length > 0 && <div className="history-credit-activity-pages">
              <button type="button" className="quiet small" disabled={activityLoading || activityOffset === 0} onClick={() => onChangeActivityOffset?.(Math.max(0, activityOffset - activity.limit))}>Newer activity</button>
              <span className="small muted">{activityOffset === 0 ? "Most recent changes" : "Earlier changes"}</span>
              <button type="button" className="quiet small" disabled={activityLoading || !activity.has_more} onClick={() => onChangeActivityOffset?.(activityOffset + activity.limit)}>Older activity</button>
            </div>}
          </div>
        </details> : <p className="small muted"><Link to={creditDetailsLink(summary?.portal_student_id, id)}>View activity for this block</Link></p>}
      </article>;
    })}
  </>;
}

function creditActivityLabel(type: LessonCreditActivity["entries"][number]["transaction_type"]): string {
  const labels: Record<LessonCreditActivity["entries"][number]["transaction_type"], string> = {
    grant: "Credit added",
    use: "Credit used for a lesson",
    restore: "Credit restored",
    adjustment: "Balance adjustment",
    reversal: "Credit change reversed",
  };
  return labels[type] ?? "Credit change";
}

function creditBlockStatusLabel(status: string): string {
  const labels: Record<string, string> = { active: "Active", requires_review: "Under school review", expired: "Expired", depleted: "Used", cancelled: "Cancelled", refunded: "Refunded" };
  return labels[status] ?? "Status unavailable";
}

function Purchase({
  purchase,
  studentId,
  studentName,
  blockStatus = null,
}: {
  purchase: PurchaseHistoryEntry;
  studentId?: string;
  studentName: string;
  blockStatus?: string | null;
}) {
  const bought = purchase.credits_purchased;
  const left = purchase.available_credits;
  const closedLabel = closedBlockLabel(blockStatus);
  const method = paymentMethodLabel(purchase.payment_method);
  const reference = bought === 1 ? "1 lesson" : `${bought}-lesson block`;

  return (
    <div className="slot history-entry">
      <div className="history-entry-main">
        <div className="history-entry-title">
          {purchase.service_name} · {reference}
        </div>
        <div className="small muted">
          {studentName} · {formatDate(purchase.purchased_at)}
        </div>
        <div className="small muted">
          {formatMoney(purchase.payment_amount, purchase.payment_currency)} ·{" "}
          {method} · {paymentRecordLabel(purchase.channel)}
        </div>
        <Link to={creditDetailsLink(studentId, purchase.lesson_block_id)}>
          View credit details
        </Link>
      </div>
      {closedLabel
        ? <span className="tag history-entry-state">{closedLabel}</span>
        : blockStatus === null
          ? <span className="tag history-entry-state">Recorded balance: {left} · current availability not checked</span>
          : <span className="tag history-entry-state">Block balance: {left} left</span>}
    </div>
  );
}

function Operation({
  operation,
  studentName,
}: {
  operation: OperationHistoryEntry;
  studentName: string;
}) {
  const outcome = operationOutcome(operation.status, operation.operation_type);

  return (
    <div className="slot history-entry">
      <div className="history-entry-main">
        <div className="history-entry-title">
          {operationTitle(operation.operation_type, operation.actor_kind)}
        </div>
        <div className="small muted">
          {studentName} · Lesson {formatDate(operation.requested_start)} at{" "}
          {formatTime(operation.requested_start)}
        </div>
        <div className="small muted">
          Requested {formatDate(operation.created_at)}
        </div>
      </div>
      <span
        className={
          outcome.warn
            ? "tag warn history-entry-state"
            : "tag history-entry-state"
        }
      >
        {outcome.label}
      </span>
    </div>
  );
}

/** A cancelled or refunded block can still hold ledger credits, but none of them can be used. */
export function closedBlockLabel(status: string | null | undefined): string | null {
  if (status === "cancelled") return "Block cancelled";
  if (status === "refunded") return "Block refunded";
  return null;
}

function creditStateLabel(left: number): string {
  if (left <= 0) return "All lessons used";
  return `${left} ${left === 1 ? "lesson" : "lessons"} left`;
}

function paymentRecordLabel(
  channel: PurchaseHistoryEntry["channel"] | string,
): string {
  switch (channel) {
    case "amelia_online":
      return "Online payment recorded";
    case "on_site":
      return "Paid on site · recorded in the Hub";
    case "staff_portal":
      return "Recorded by the school";
    default:
      // Runtime API data can move ahead of this client's literal union. Keep
      // the family-facing row complete without printing an internal channel.
      return "Payment recorded";
  }
}
